import clsx from 'clsx';
import {
  type ForwardedRef,
  type HTMLAttributes,
  type MouseEvent,
  type PropsWithChildren,
  forwardRef,
} from 'react';
import type { Spacing } from './layout';
import { isDefined } from '@/utils/assertions';

export interface HStackProps {
  horizontalAlignment?: 'start' | 'center' | 'full' | 'end';
  verticalAlignment?: 'start' | 'center' | 'full' | 'baseline' | 'end';
  fullWidth?: boolean;
  fullHeight?: boolean;
  flex?: 'min' | 'expand';
  gap?: Spacing;
  margin?: Spacing;
  padding?: Spacing;
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
  className?: string;
}

/**
 * Horizontal layout:
 *
 * @example
 * // +---------------------------------------------------+
 * // |                       PARENT                      |
 * // |                                                   |
 * // |  +-----+ +-----++---------------------++-------+  |
 * // |  | ONE | | TWO ||       spacer        || THREE |  |
 * // |  +-----+ +-----++---------------------++-------+  |
 * // |                     <------------->               |
 * // |                                                   |
 * // +---------------------------------------------------+
 *
 * @example
 * <HStack horizontalAlignment="start" verticalAlignment="center" fullWidth gap="8px" padding="4px">
 *   <div>ONE</div>
 *   <div>TWO</div>
 *   <Spacer />
 *   <div>THREE</div>
 * </HStack>
 */
export const HStack = forwardRef(
  (
    props: PropsWithChildren<HStackProps> & HTMLAttributes<HTMLDivElement>,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const {
      horizontalAlignment = 'full',
      verticalAlignment = 'center',
      fullWidth = false,
      fullHeight = false,
      flex = 'min',
      gap,
      margin,
      padding,
      className,
      onClick,
      children,
      ...otherProps
    } = props;

    const componentClassName = clsx(
      [
        'd-flex flex-row flex-nowrap overflow-hidden',
        isDefined(gap) ? `gap-${gap}` : undefined,
        isDefined(margin) ? `m-${margin}` : undefined,
        isDefined(padding) ? `p-${padding}` : undefined,
        className,
      ],
      {
        'justify-content-start': horizontalAlignment === 'start',
        'justify-content-center': horizontalAlignment === 'center',
        'justify-content-between': horizontalAlignment === 'full',
        'justify-content-end': horizontalAlignment === 'end',
        'align-items-start': verticalAlignment === 'start',
        'align-items-center': verticalAlignment === 'center',
        'align-items-baseline': verticalAlignment === 'baseline',
        'align-items-stretch': verticalAlignment === 'full',
        'align-items-end': verticalAlignment === 'end',
        'flex-grow-0 flex-shrink-0': flex === 'min',
        'flex-fill': flex === 'expand',
        'w-100': fullWidth,
        'h-100': fullHeight,
      },
    );

    return (
      <div ref={ref} onClick={onClick} className={componentClassName} {...otherProps}>
        {children}
      </div>
    );
  }
);
